import { useEffect, useRef, useState } from 'react';

// Tracks in-flight autosave operations so the UI can show a "saving / saved"
// status. Adapted from the portal's useSaveStack, trimmed to what this admin
// form needs (no full-screen modal spinner).

export type StackItem = {
  id: string;
  userAction: string;
};

export type SaveStack = {
  stackItems: Map<string, StackItem>;
  lastSavedTimestamp: Date | null;
  pushToSaveStack: (item: StackItem) => void;
  removeFromSaveStack: (id: string) => void;
  showCheckmark: boolean;
};

const CHECKMARK_VISIBLE_MS = 60_000;

export function useSaveStack(): SaveStack {
  const [stackItems, setStackItems] = useState<Map<string, StackItem>>(() => new Map());
  const [lastSavedTimestamp, setLastSavedTimestamp] = useState<Date | null>(null);
  const [showCheckmark, setShowCheckmark] = useState(false);
  const checkmarkTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const hasPendingSaves = stackItems.size > 0;

  // Warn before leaving if a save is still in flight.
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasPendingSaves) e.preventDefault();
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasPendingSaves]);

  useEffect(() => {
    return () => {
      if (checkmarkTimeoutRef.current) clearTimeout(checkmarkTimeoutRef.current);
    };
  }, []);

  const pushToSaveStack = (item: StackItem) => {
    setStackItems((prev) => {
      const next = new Map(prev);
      next.set(item.id, item);
      return next;
    });
  };

  const removeFromSaveStack = (id: string) => {
    setStackItems((prev) => {
      const next = new Map(prev);
      next.delete(id);
      if (next.size === 0) {
        setShowCheckmark(true);
        if (checkmarkTimeoutRef.current) clearTimeout(checkmarkTimeoutRef.current);
        checkmarkTimeoutRef.current = setTimeout(() => setShowCheckmark(false), CHECKMARK_VISIBLE_MS);
      }
      return next;
    });
    setLastSavedTimestamp(new Date());
  };

  return {
    stackItems,
    lastSavedTimestamp,
    pushToSaveStack,
    removeFromSaveStack,
    showCheckmark,
  };
}
